import { useCallback, useMemo } from 'react';
import { matchPath } from 'react-router';
import _groupBy from 'lodash/groupBy';
import _isBoolean from 'lodash/isBoolean';
import _isDate from 'lodash/isDate';
import _isArray from 'lodash/isArray';
import _isObject from 'lodash/isObject';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isEqualWith from 'lodash/isEqualWith';
import _map from 'lodash/map';
import _pickBy from 'lodash/pickBy';
import _includes from 'lodash/includes';
import _omit from 'lodash/omit';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _find from 'lodash/find';
import _findKey from 'lodash/findKey';
import _filter from 'lodash/filter';
import _reduce from 'lodash/reduce';
import _sortBy from 'lodash/sortBy';
import _orderBy from 'lodash/orderBy';
import _uniqBy from 'lodash/uniqBy';
import moment, { Moment } from 'moment';
import {
  gqlDma,
  gqlRegion,
  gqlFacetBucket,
  gqlCountryEnum as COUNTRY,
  gqlTalentUnionEnum as TALENT_UNION,
  gqlSkillLevelEnum as SKILL_LEVEL,
} from '@minecraft.graphql-types';
import { SelectOption as DropdownOption } from '@minecraft.typings';
import { haveSameContents, helpers } from '@minecraft.utils';
import { dateFormat } from '@minecraft.date-utils';
import { useTranslation } from '@blocs.i18n';
import {
  filtersKeySuffix,
  dependentFilters,
  emptyOption,
  defaultExcludeFilters,
  defaultExcludeFilterValues,
} from '../config';
import { FiltersNameEnum, RangeEnum, RangeType, FilterValues, SelectOption } from '../types';
import * as saveFilterUtilities from './saveFilterUtilities';

const SAG_AFTRA_CHILDREN_CODES = [TALENT_UNION.MEMBER, TALENT_UNION.ELIGIBLE, TALENT_UNION.FICORE];

const { getAllowedLocations } = helpers;

export type PillType = {
  name: string | { label: string };
  value: any;
};

export type PreparePillsArgs = {
  filtersLabel: FilterValues;
  filters: FilterValues;
  excludeValues?: string[];
};

export type PreparePillsResult = {
  [key: string]: PillType;
};

export type GetRepresentativeNameArgs = {
  divisionName?: string;
  companyName: string;
};

export type GetMeasureArgs = {
  switchTo?: RangeType['switchTo'];
  type?: RangeType['type'];
};

export type GetRangeTextArgs = RangeType;

export type LocationByRegionType = {
  id: gqlDma['id'];
  name: gqlRegion['name'];
  cities: {
    id: gqlDma['id'];
    name: gqlDma['name'];
  }[];
};

export type GetLocationsArgs = {
  dmas: gqlDma[];
};

export type GetLocationsResult<T = LocationByRegionType[]> = {
  usLocations?: T;
  caLocations?: T;
  euLocations?: T;
  auLocations?: T;
  laLocations?: T;
  additionalLocations?: T;
};

export type GetClearedFilterArgs = {
  filterKey: FiltersNameEnum;
  initialFilters: FilterValues;
};

const predicate = <T>(value: T[keyof T]): T[keyof T] | void => {
  if (
    (_isBoolean(value) && value) ||
    (!_isBoolean(value) && !_isObject(value) && !_isEmpty(value)) ||
    (!_isBoolean(value) && _isArray(value) && !_isEmpty(value)) ||
    // Additional check for search with dropdown
    (!_isBoolean(value) && _isObject(value) && !_isArray(value) && value.inputValue) ||
    // Additional check for range fields
    (!_isBoolean(value) &&
      _isObject(value) &&
      !_isArray(value) &&
      !_isObject(value.min || value.max) &&
      (value.min || value.max)) ||
    // Additional check for date range fields
    (!_isBoolean(value) && _isObject(value) && !_isArray(value) && _isDate(value.min || value.max)) ||
    // Additional check for size range fields
    (!_isBoolean(value) &&
      _isObject(value) &&
      !_isArray(value) &&
      _isObject(value.min || value.max) &&
      (value.min?.label || value.max?.label)) ||
    // Additional check for radio buttons
    (!_isBoolean(value) && _isObject(value) && !_isArray(value) && value.label) ||
    (_isObject(value) && ((_isArray(value.list) && !_isEmpty(value.list)) || !_isEmpty(value.note)))
  ) {
    return value;
  }

  return undefined;
};

export const excludeInvalidFilters = <Values = FilterValues>(appliedFilters: Values): Partial<Values> =>
  _pickBy(appliedFilters as FilterValues, predicate) as Partial<Values>;

export const removeTimeZoneFromDate = (date: Date | string): Moment | void => {
  let isoString = (date as Date)?.toISOString?.();

  // some date strings are not in an ISO format
  // build them as JS Date() objects and format to ISO strings
  if (!isoString && typeof date === 'string') {
    isoString = new Date(date).toISOString();
  }

  return isoString ? moment(isoString).utc().add(moment(isoString).utcOffset(), 'm') : undefined;
};

export const safeRemoveTimeZoneFromDate = (date: Date): Moment | void => {
  if (!date) return undefined;

  const currentDate = moment(date);
  const offset = currentDate.utcOffset();

  return currentDate.utc(true).endOf('day').subtract(offset, 'm');
};

export const isEqualCustomizer = <Values = FilterValues>(value1: Values, value2: Values): boolean => {
  const keys = Object.keys(value1);

  return _every(keys, (key) => {
    if (_isArray(value1[key]) && _isArray(value2[key])) {
      return haveSameContents(value1[key], value2[key]);
    }

    return _isEqual(value1[key], value2[key]);
  });
};

export const shouldClearButtonBeVisible = ({
  appliedFilterPills,
  appliedFilters,
  retainedFilters,
  initialFilters,
}): boolean => {
  if (!_isEmpty(appliedFilterPills)) return true;

  if (
    _isEqualWith(appliedFilters, initialFilters, isEqualCustomizer) &&
    !_isEqualWith(retainedFilters, initialFilters, isEqualCustomizer)
  ) {
    return true;
  }

  return false;
};

export const getRepresentativeName = ({ divisionName, companyName }: GetRepresentativeNameArgs): string => {
  if (divisionName) {
    return `${companyName}${` (${divisionName})`}`;
  }

  return companyName;
};

export const getUnionName = (originalName: string, code: TALENT_UNION): string => {
  if (SAG_AFTRA_CHILDREN_CODES.includes(code)) {
    return `${TALENT_UNION.SAG_AFTRA} ${originalName}`;
  }

  return originalName;
};

export const getLocationByRegion = (locations: gqlDma[]): LocationByRegionType[] => {
  if (locations.length === 0) return [];

  const groupedLocationsByRegion = _groupBy(locations, 'region.id');

  return _map(groupedLocationsByRegion, (dma) => ({
    id: dma[0].region.id,
    name: dma[0].region.name,
    cities: _uniqBy(
      _map(dma, ({ id, name }) => ({
        id,
        name,
      })),
      'id'
    ),
  }));
};

export const getLocations = ({ dmas }: GetLocationsArgs): GetLocationsResult => {
  if (_isEmpty(dmas)) return {};

  const sortedLocations = _sortBy(dmas, 'name');
  const { isUsCaAllowed, isGbIeAllowed, isEuAllowed, isAuNzAllowed, areAdditionalAllowed, isLAAllowed } =
    getAllowedLocations();

  return {
    ...(isUsCaAllowed && {
      usLocations: getLocationByRegion(
        _filter(sortedLocations, (location) => _includes([COUNTRY.US], location.region.country.code))
      ),
    }),
    ...(isUsCaAllowed && {
      caLocations: getLocationByRegion(
        _filter(sortedLocations, (location) => _includes([COUNTRY.CA], location.region.country.code))
      ),
    }),

    ...(isGbIeAllowed && {
      euLocations: getLocationByRegion(
        _filter(sortedLocations, (location) => _includes([COUNTRY.GB, COUNTRY.IE], location.region.country.code))
      ),
    }),
    ...(isAuNzAllowed && {
      auLocations: getLocationByRegion(
        _filter(sortedLocations, (location) => _includes([COUNTRY.AU, COUNTRY.NZ], location.region.country.code))
      ),
    }),
    ...(isLAAllowed && {
      laLocations: getLocationByRegion(
        _filter(sortedLocations, (location) =>
          _includes(
            [COUNTRY.MX, COUNTRY.BR, COUNTRY.VE, COUNTRY.PE, COUNTRY.CL, COUNTRY.AR, COUNTRY.CO],
            location.region.country.code
          )
        )
      ),
    }),
    ...((areAdditionalAllowed || isEuAllowed) && {
      additionalLocations: getLocationByRegion(
        _filter(sortedLocations, (location) =>
          _includes(
            [...(areAdditionalAllowed ? [COUNTRY.ZA] : []), ...(isEuAllowed ? [COUNTRY.FR, COUNTRY.ES] : [])],
            location.region.country.code
          )
        )
      ),
    }),
  };
};

const useLazyMeasure = () => {
  const { t } = useTranslation();

  return useCallback(
    ({ type, switchTo }: GetMeasureArgs): string => {
      switch (type) {
        case RangeEnum.age:
          return switchTo ? t('common:form.label.months') : t('common:form.label.years');
        case RangeEnum.height:
          return switchTo ? t('common:measurement.unit.cm') : t('common:measurement.unit.ftAndIn');
        case RangeEnum.weight:
          return switchTo ? t('common:measurement.unit.kg') : t('common:measurement.unit.lbs');
        case RangeEnum.size:
          return switchTo ? t('common:measurement.unit.cm') : t('common:measurement.unit.in');
        default:
          return '';
      }
    },
    [t]
  );
};

export const useMeasure = (args: GetMeasureArgs): string => {
  const lazyMeasure = useLazyMeasure();

  return lazyMeasure(args);
};

const useLazyRangeText = () => {
  const { t } = useTranslation();
  const lazyMeasure = useLazyMeasure();

  return useCallback(
    ({ switchTo, min, max, type }: GetRangeTextArgs): string | void => {
      const measure = lazyMeasure({ switchTo, type });
      const measureString = measure ? ` ${measure}` : '';
      const fromPrimary = _isObject(min) ? min?.label : min;
      const toPrimary = _isObject(max) ? max?.label : max;

      const from =
        type === RangeEnum.date
          ? (removeTimeZoneFromDate(fromPrimary) as Moment)?.format(dateFormat.SHORT_FORMAT)
          : fromPrimary;
      const to =
        type === RangeEnum.date
          ? (removeTimeZoneFromDate(toPrimary) as Moment)?.format(dateFormat.SHORT_FORMAT)
          : toPrimary;

      if (from && !to) {
        return t('common:filter.fromItem', { from: `${from}${measureString}` });
      }

      if (to && !from) {
        return t('common:filter.toItem', { to: `${to}${measureString}` });
      }

      if (from && to) {
        return t('common:filter.fromToItem', { from, to: `${to}${measureString}` });
      }

      return undefined;
    },
    [t, lazyMeasure]
  );
};

export const useRangeText = (args: GetRangeTextArgs): string | void => {
  const lazyRangeText = useLazyRangeText();

  return lazyRangeText(args);
};

const excludeFilterValues = (filterkey: FiltersNameEnum, options: SelectOption[]): SelectOption[] => {
  if (defaultExcludeFilterValues[filterkey]) {
    return _filter(options, (option) => !_includes(defaultExcludeFilterValues[filterkey], option.value));
  }

  return options;
};

export const useFilterPills = ({ filtersLabel, filters, excludeValues = [] }: PreparePillsArgs): PreparePillsResult => {
  const cleanFilters = _omit(filters, [...defaultExcludeFilters, ...excludeValues]);
  const keys = Object.keys(excludeInvalidFilters(cleanFilters));
  const lazyRangeText = useLazyRangeText();

  return useMemo(
    () =>
      Object.fromEntries(
        _map(keys, (key) => {
          if (
            _isObject(filters[key]) &&
            !_isArray(filters[key]) &&
            _isEmpty(filters[key].list) &&
            _isEmpty(filters[key].note)
          ) {
            const value =
              filters[key]?.inputValue ||
              lazyRangeText({
                switchTo: filters[key]?.switchTo,
                min: filters[key].min?.toString(),
                max: filters[key].max?.toString(),
                type: filters[key]?.type,
              }) ||
              filters[key]?.label;

            return [
              [key],
              {
                name: filtersLabel[key],
                value,
              },
            ];
          }

          return [
            [key],
            {
              name: filtersLabel[key],
              value: excludeFilterValues(key as FiltersNameEnum, filters[key]),
            },
          ];
        })
      ),
    [filters, filtersLabel, keys, lazyRangeText]
  );
};

const getLocationPathWithoutParams = (location: string): string => {
  const target = '?';

  return _includes(location, target) ? location.slice(0, location.lastIndexOf(target)) : location;
};

export const getFiltersSessionStorageKeys = (sessionStorageKeys: string[]): string[] => {
  return _filter(sessionStorageKeys, (key) => _includes(key, filtersKeySuffix));
};

export const shouldFiltersSessionStorageBeCleared = ({
  withMatchPath,
  newLocation,
  prevLocation,
  additionalLocation: primaryAdditionalLocation,
  matchPathValue,
}): boolean => {
  const additionalLocation = getLocationPathWithoutParams(primaryAdditionalLocation);

  // do not clear session storage if matchPathValue property matches new location
  if (withMatchPath && !_isEmpty(matchPathValue)) {
    return !_some(matchPathValue, (matchPathValueItem) =>
      matchPath(newLocation, {
        path: matchPathValueItem,
        exact: true,
        strict: false,
      })
    );
  }

  if (prevLocation !== newLocation && additionalLocation !== newLocation) {
    return true;
  }

  return false;
};

export const getOptionsWithEmptyOption = (options: SelectOption[]): SelectOption[] => {
  return [emptyOption, ...(options || [])];
};

export const getFilteredArrayByEnabled = (array: Record<string, any>[]): Record<string, any>[] => {
  return _filter(array, { enabled: true });
};

export const generateSelectOptionsBasedOnField = (
  array: Record<string, any>[] | readonly Record<string, any>[],
  fieldName: string,
  valueAsString = false,
  counts?: gqlFacetBucket[],
  valueBasedOnCode = false,
  fieldAdditionalData?: Record<string, any>
): Array<DropdownOption & SelectOption> => {
  const withCounts = _isArray(counts);

  return _map(array, (item) => {
    const fieldCount = withCounts && (_find(counts, { key: item.id })?.docCount || 0);
    const valueAsCode = item?.code;
    const valueAsId = valueAsString ? item?.id?.toString() : item?.id;

    return {
      label: item[fieldName],
      value: valueBasedOnCode ? valueAsCode : valueAsId,
      ...(withCounts && {
        count: fieldCount,
      }),
      ...fieldAdditionalData,
    };
  });
};

type OptionWithCounts = SelectOption & { count: number };
type LazyGenerateSkillLevelsSelectOptionsHook = () => (
  array: Record<string, any>[],
  counts?: gqlFacetBucket[]
) => Array<OptionWithCounts>;

export const useLazyGenerateSkillLevelsSelectOptions: LazyGenerateSkillLevelsSelectOptionsHook = (
  isFromSkillDrawer = false
) => {
  const { t } = useTranslation();

  return useCallback(
    (array: Record<string, any>[], counts?: gqlFacetBucket[]) => {
      const withCounts = _isArray(counts);

      const skillLevelsMap = {
        [SKILL_LEVEL.BEGINNER]: t('common:skill.level.beginner'),
        [SKILL_LEVEL.INTERMEDIATE]: t('common:skill.level.intermediate'),
        [SKILL_LEVEL.EXPERT]: t('common:skill.level.expert'),
      };

      const getSkillLevelIds = ({ skillLevels, skillLevelsCodes }) => {
        return _reduce(
          skillLevels,
          (acc, skillLevel) => {
            if (_includes(skillLevelsCodes, skillLevel.code)) {
              return [...acc, skillLevel.id];
            }

            return acc;
          },
          []
        );
      };
      const getCombinedCounters = (skillLevelIds) => {
        return _reduce(
          skillLevelIds,
          (acc, skillLevelId) => {
            return acc + Number(_find(counts, { key: skillLevelId })?.docCount || 0);
          },
          0
        );
      };

      const getSkillCounter = (id) => {
        const [result] = counts.filter((item) => item?.key === id) || [];

        return result?.docCount;
      };

      return _map(array, (item) => {
        let skillLevelIds = [];

        if (item.code === SKILL_LEVEL.BEGINNER) {
          skillLevelIds = getSkillLevelIds({
            skillLevels: array,
            skillLevelsCodes: [SKILL_LEVEL.BEGINNER, SKILL_LEVEL.INTERMEDIATE, SKILL_LEVEL.EXPERT],
          });

          return {
            label: skillLevelsMap[item.code],
            value: skillLevelIds,
            ...(withCounts && {
              count: isFromSkillDrawer ? getSkillCounter(item?.id) : getCombinedCounters(skillLevelIds),
            }),
          };
        }

        if (item.code === SKILL_LEVEL.INTERMEDIATE) {
          skillLevelIds = getSkillLevelIds({
            skillLevels: array,
            skillLevelsCodes: [SKILL_LEVEL.INTERMEDIATE, SKILL_LEVEL.EXPERT],
          });

          return {
            label: skillLevelsMap[item.code],
            value: skillLevelIds,
            ...(withCounts && {
              count: isFromSkillDrawer ? getSkillCounter(item?.id) : getCombinedCounters(skillLevelIds),
            }),
          };
        }

        skillLevelIds = getSkillLevelIds({
          skillLevels: array,
          skillLevelsCodes: [SKILL_LEVEL.EXPERT],
        });

        return {
          label: skillLevelsMap[item.code],
          value: skillLevelIds,
          ...(withCounts && {
            count: isFromSkillDrawer ? getSkillCounter(item?.id) : getCombinedCounters(skillLevelIds),
          }),
        };
      });
    },
    [t]
  );
};

export const groupDataForHierarchySelect = (dataCollection, counts?: gqlFacetBucket[]) => {
  if (!dataCollection) return [];

  const withCounts = _isArray(counts);

  return _map(_orderBy(dataCollection, ['name'], ['asc']), (dataObj) => {
    const fieldCount = withCounts && (_find(counts, { key: dataObj.id })?.docCount || 0);

    return {
      value: dataObj.id,
      label: dataObj.name,
      ...(dataObj?.childObjects && {
        options: groupDataForHierarchySelect(
          _filter(dataObj.childObjects, (x) => x.selectable),
          counts
        ),
      }),
      ...(!dataObj?.childObjects &&
        withCounts && {
          count: fieldCount,
        }),
    };
  });
};

const filterSubDocsByCountry = (subdocs, countries) =>
  _filter(subdocs, (subdoc) => _includes(countries, subdoc.country.code));

export const getUpdatedParentDocs = (parentDocs, countries) =>
  _filter(
    parentDocs.map((doc) => ({
      ...doc,
      ...{ subdocs: _sortBy(filterSubDocsByCountry(doc.subdocs, countries), 'name') },
    })),
    (parentDoc) => !!parentDoc.subdocs && !_isEmpty(parentDoc.subdocs)
  );

export const shouldNonUnionBeChecked = (unionStatus: SelectOption[]): boolean => {
  return Boolean(_find(unionStatus, { value: TALENT_UNION.NON_UNION }));
};

export const getClearedFilter = ({ filterKey, initialFilters }: GetClearedFilterArgs): Partial<FilterValues> => {
  return {
    // initially the filter can have several values, in this case this check is needed to force a clear
    [filterKey]: _isArray(initialFilters[filterKey]) ? [] : initialFilters[filterKey],
    ...(dependentFilters[filterKey] && {
      ...Object.fromEntries(
        _map(dependentFilters[filterKey], (dependentFilterKey) => [
          dependentFilterKey,
          initialFilters[dependentFilterKey],
        ])
      ),
    }),
  };
};

export const getDependentFilterKey = (filterKey: FiltersNameEnum): string => {
  return _findKey(dependentFilters, (dependentFilterKeyValue) => _includes(dependentFilterKeyValue, filterKey));
};

export { saveFilterUtilities };
